import { ENTRYPOINT } from '../../../config/entrypoint';
import { API_ROUTES } from '../../../constants/api_routes';
import { postData, getData, getHeaders, deleteData, putData, patchData } from '../../../http/request';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, set, format } from 'date-fns';
import { CONTENT_TYPE, DATE_FORMAT, TOKEN_TYPE } from '../../../constants/constants';
import moment from "moment";

export const APPOINTMENTS_ACTION_TYPES = {
	GET_APPOINTMENTS_SUCCESS: 'GET_APPOINTMENTS_SUCCESS',
    GET_APPOINTMENT_SUCCESS: 'GET_APPOINTMENT_SUCCESS',
    POST_APPOINTMENT_SUCCESS: 'POST_APPOINTMENT_SUCCESS',
    PUT_APPOINTMENT_SUCCESS: 'PUT_APPOINTMENT_SUCCESS',
    PATCH_APPOINTMENT_SUCCESS: 'PATCH_APPOINTMENT_SUCCESS',
    DELETE_APPOINTMENT_SUCCESS: 'DELETE_APPOINTMENT_SUCCESS'
};

const APPOINTMENTS_URL = `${ENTRYPOINT}${API_ROUTES.APPOINTMENTS}`;
const DIGITAL_RECORDS_URL = `${ENTRYPOINT}${API_ROUTES.DIGITAL_RECORDS}`;
const CUSTOMER_DIGITAL_RECORD_URL = `${ENTRYPOINT}${API_ROUTES.CUSTOMER_DIGITAL_RECORD}`;
const PRINT_CUSTOMER_DIGITAL_RECORD_URL = `${ENTRYPOINT}${API_ROUTES.PRINT_CUSTOMER_DIGITAL_RECORD}`;
const PRINT_SINGLE_CUSTOMER_DIGITAL_RECORD_URL = `${ENTRYPOINT}${API_ROUTES.PRINT_SINGLE_CUSTOMER_DIGITAL_RECORD}`;
const PRINT_RECORD_URL = `${ENTRYPOINT}${API_ROUTES.PRINT_RECORD}`;

const getParams = ({ customer, worker, place, date, view }) => {
    let params = '?';
    if (date && view === 'Month') {
        const startDate = format(startOfMonth(date), DATE_FORMAT)
        const endDate = format(addDays(endOfMonth(date), 1), DATE_FORMAT);
        // let endDate = endOfMonth(date);
        // endDate.setDate(endDate.getDate() + 1);
        // endDate = format(endOfMonth(date), DATE_FORMAT);
        params = `${params}startDate[after]=${startDate}&endDate[before]=${endDate}`;
    }
    else if (date && view === 'Week') {
        const startDate = format(startOfWeek(date, { weekStartsOn: 1 }), DATE_FORMAT);
        const endDate = format(endOfWeek(date, { weekStartsOn: 1 }), DATE_FORMAT);
        params = `${params}startDate[after]=${startDate}&endDate[before]=${endDate}`;
    }
    else if (date && view === 'Day') {
        const startDate = format(startOfDay(date), DATE_FORMAT);
        const endDate = format(endOfDay(addDays(date, 1)), DATE_FORMAT);
        params = `${params}startDate[after]=${startDate}&endDate[before]=${endDate}`;
    }
    if (customer) {
        const { uuid: customer_uuid } = customer;
        params = `${params}&customer=${customer_uuid}`;
    }
    if (worker) {
        const { uuid: worker_uuid } = worker;
        params = `${params}&worker=${worker_uuid}`;
    }
    if (place) {
        const { uuid: place_uuid } = place;
        params = `${params}&place=${place_uuid}`;
    }
    return params;
}

const formatData = (data) => {
    if (data.startDate) {
        // Set seconds to 0 since it causes violation
        data.startDate = set(data.startDate, { seconds: 0 });
        data.startDate = moment(data.startDate).toLocaleString();
    }
    if (data.endDate) {
        // Set seconds to 0 since it causes violation
        data.endDate = set(data.endDate, { seconds: 0 });
        data.endDate = moment(data.endDate).toLocaleString();
    }
    return data;
}

export const getAppointments = async (dispatch, data) => {
    const params = getParams(data);
    const GET_APPOINTMENTS_URL = `${APPOINTMENTS_URL}${params}`;
    const response = await getData(GET_APPOINTMENTS_URL, getHeaders());
    dispatch({ type: APPOINTMENTS_ACTION_TYPES.GET_APPOINTMENTS_SUCCESS, payload: response });
}

export const getAppointment = (uuid) =>
    async (dispatch) => {
        const GET_APPOINTMENT_URL = `${APPOINTMENTS_URL}/${uuid}`;
        const response = await getData(GET_APPOINTMENT_URL);
        dispatch({ type: APPOINTMENTS_ACTION_TYPES.GET_APPOINTMENT_SUCCESS, payload: response });
    }

export const postAppointment = (data) => (
    async (dispatch) => {
        const response = await postData(APPOINTMENTS_URL, formatData(data));
        if (!(response.violations)) {
            dispatch({ type: APPOINTMENTS_ACTION_TYPES.POST_APPOINTMENT_SUCCESS, payload: response });
        }
        return response;
    });

export const putAppointment = (appointment) => (
    async (dispatch) => {
        const uuid = appointment.id;
        const PUT_APPOINTMENT_URL = `${APPOINTMENTS_URL}/${uuid}`;
        const response = await putData(PUT_APPOINTMENT_URL, formatData(appointment));
        dispatch({ type: APPOINTMENTS_ACTION_TYPES.PUT_APPOINTMENT_SUCCESS, payload: response });
        return response;
    }
);

export const patchAppointment = (appointment) => (
    async (dispatch) => {
        const uuid = appointment.id;
        const PATCH_APPOINTMENT_URL = `${APPOINTMENTS_URL}/${uuid}`;
        const response = await patchData(PATCH_APPOINTMENT_URL, appointment);
        dispatch({ type: APPOINTMENTS_ACTION_TYPES.PATCH_APPOINTMENT_SUCCESS, payload: response });
        return response;
    }
);

export const deleteAppointment = async (dispatch, uuid) => {
    const DELETE_APPOINTMENT_URL = `${APPOINTMENTS_URL}/${uuid}`;
    await deleteData(DELETE_APPOINTMENT_URL);
    dispatch({ type: APPOINTMENTS_ACTION_TYPES.DELETE_APPOINTMENT_SUCCESS, payload: uuid });
}

export const postDigitalRecord = (data) => (
    async (dispatch) => {
        const response = await postData(DIGITAL_RECORDS_URL, data);
        // Update appointment in appointments reducer
        dispatch(getAppointment(response.appointment.uuid));
        return response;
    }
);

export const putDigitalRecord = (data) => (
    async (dispatch) => {
        const uuid = data.uuid;
        const PUT_DIGITAL_RECORD_URL = `${DIGITAL_RECORDS_URL}/${uuid}`;
        const response = await putData(PUT_DIGITAL_RECORD_URL, data);
        // Update appointment in appointments reducer
        dispatch(getAppointment(response.appointment.uuid));
        return response;
    }
);

export const deleteDigitalRecord = async (dispatch, digitalRecordUuid, appointmentUuid) => {
    const DELETE_DIGITAL_RECORD_URL = `${DIGITAL_RECORDS_URL}/${digitalRecordUuid}`;
    await deleteData(DELETE_DIGITAL_RECORD_URL);
    dispatch(getAppointment(appointmentUuid));
}

export const printRecord = async (digitalRecordUuid) => {
    const URL = `${PRINT_RECORD_URL}?digitalRecord=${digitalRecordUuid}`;
    return await getData(URL);
}

export const getCustomerDigitalRecord = async ({ customerUuid, page, rowsPerPage }) => {
    const GET_CUSTOMER_DIGITAL_RECORD_URL = `${CUSTOMER_DIGITAL_RECORD_URL}?page=${page+1}&itemsPerPage=${rowsPerPage}&customer=${customerUuid}`;
    const response = await getData(GET_CUSTOMER_DIGITAL_RECORD_URL, getHeaders(TOKEN_TYPE.TOKEN, CONTENT_TYPE.LD_JSON));
    return response;
}

export const printCustomerDigitalRecord = async (customerUuid) => {
    const URL = `${PRINT_CUSTOMER_DIGITAL_RECORD_URL}?customer=${customerUuid}`;
    return await getData(URL);
}

export const printSingleCustomerDigitalRecord = async (digitalRecordUuid, customerUuid) => {
    const URL = `${PRINT_SINGLE_CUSTOMER_DIGITAL_RECORD_URL}?digitalRecord=${digitalRecordUuid}&customer=${customerUuid}`;
    return await getData(URL);
}
